import React, { useCallback, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { DangerZoneAction } from '../../../components/design-system';
import {
  DELETE_SITE_MUTATION,
  GET_MY_SITES_QUERY,
  updateSiteFindsInCache,
} from '../../../gql';
import {
  SiteDeleteAction,
  SiteDeleteDialog,
  SiteDeleteRequest,
} from './SiteDeleteDialog';

interface SiteDeleteControlProps {
  siteId: string;
}

interface SiteOption {
  canAddFinds: boolean;
  id: string;
  name?: string | null;
}

const DELETE_ALL: SiteDeleteAction = 'delete-all';
const DELETE_SITE_ONLY: SiteDeleteAction = 'delete-site-only';
const selectionOptions = [
  {
    label: (
      <FormattedMessage
        id="site.delete.all"
        defaultMessage="Delete site and finds"
      />
    ),
    value: DELETE_ALL,
  },
  {
    label: (
      <FormattedMessage
        id="site.delete.siteOnly"
        defaultMessage="Delete site only"
      />
    ),
    value: DELETE_SITE_ONLY,
  },
];

const SiteDeleteControl = React.memo(({ siteId }: SiteDeleteControlProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [action, setAction] = useState<SiteDeleteAction>(DELETE_ALL);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string>();
  const { data } = useQuery(GET_MY_SITES_QUERY);
  const [deleteSite, { loading }] = useMutation(DELETE_SITE_MUTATION, {
    refetchQueries: [{ query: GET_MY_SITES_QUERY }],
  });
  const destinations = useMemo(
    () =>
      ((data?.getMySites || []) as SiteOption[])
        .filter(
          (site) => site.id !== siteId && site.canAddFinds && Boolean(site.name)
        )
        .map((site) => ({ id: site.id, name: site.name! })),
    [data, siteId]
  );
  const actionLabel =
    selectionOptions.find(({ value }) => value === action)?.label || null;

  const handleActionChange = useCallback((value: string) => {
    if (value === DELETE_ALL || value === DELETE_SITE_ONLY) {
      setAction(value);
    }
  }, []);
  const handleDialogOpen = useCallback(() => {
    setError(undefined);
    setDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => {
    if (!loading) {
      setDialogOpen(false);
      setError(undefined);
    }
  }, [loading]);
  const handleConfirm = useCallback(
    async (request: SiteDeleteRequest) => {
      setError(undefined);

      try {
        const { data: mutationData } = await deleteSite({
          variables: { input: { siteId, ...request } },
          update: (cache, { data: deleteData }) => {
            const result = deleteData?.deleteSite;

            if (result?.result) {
              updateSiteFindsInCache(
                cache,
                siteId,
                result.targetSiteId ?? null,
                request.deleteFinds
              );
            }
          },
        });
        const result = mutationData?.deleteSite;

        if (result?.result) {
          navigate('/s');

          return;
        }

        setError(
          result?.errors?.[0]?.error ||
            intl.formatMessage({
              id: 'site.delete.failed',
              defaultMessage: 'The site could not be deleted.',
            })
        );
      } catch (mutationError) {
        setError(
          mutationError instanceof Error
            ? mutationError.message
            : intl.formatMessage({
                id: 'site.delete.failed',
                defaultMessage: 'The site could not be deleted.',
              })
        );
      }
    },
    [deleteSite, intl, navigate, siteId]
  );

  return (
    <>
      <DangerZoneAction
        actionIcon={<DeleteOutlineIcon />}
        actionLabel={actionLabel}
        active={active}
        disabled={loading}
        label={
          <FormattedMessage
            id="site.delete.enable"
            defaultMessage="Delete site"
          />
        }
        onAction={handleDialogOpen}
        onActiveChange={setActive}
        onSelectedActionChange={handleActionChange}
        selectedAction={action}
        selectionOptions={selectionOptions}
      />
      <SiteDeleteDialog
        action={action}
        destinations={destinations}
        error={error}
        loading={loading}
        onClose={handleDialogClose}
        onConfirm={handleConfirm}
        open={dialogOpen}
      />
    </>
  );
});

SiteDeleteControl.displayName = 'SiteDeleteControl';

export { SiteDeleteControl };
